import {SUCCUBES_SEDUCTION_RESULT_COLOR} from './factions/succubes';
import {GOBLINS_ABILITY_RESULT_COLOR} from './factions/goblins';
import {UNIT_TYPES} from './units';

/**
 * АНИМАЦИИ СПОСОБНОСТЕЙ ФРАКЦИЙ на поле (рисует scenes/AbilityCastAnimation,
 * расчет поз — utils/effects/buildAbilityCast, когда показывать —
 * scenes/playAbilityCastEffects):
 *  - Соблазн суккубов: отряд собирает вокруг себя сердечки, к цели летит
 *    пульсирующее сердце. Успех — сердце лопается волной и россыпью сердечек,
 *    над целью всплывает большое сердце; провал — сердце бьется о невидимый
 *    щит, раскалывается надвое, половинки серея падают, остается дымок.
 *  - Бомба гоблинов (повреждение строения): бомба с горящим фитилем летит по
 *    дуге к строению. Повредила — взрыв, видный сверху: круг ударной волны,
 *    дым и угольки расходятся во все стороны, остается выжженное пятно, камера
 *    вздрагивает; разрушила — взрыв крупнее, второй взрыв следом и дрожь сильнее; провал —
 *    бомба подпрыгивает, фитиль тухнет, и с небольшой задержкой бомба исчезает.
 *  - Ловушка гоблинов: к ячейке летит облачко блесток. Удалась — контур
 *    ячейки вспыхивает цветом гоблинов и защелкивается; не удалась — серый дым
 *    и обломки. Ячейку ловушки видит только ее владелец (как и саму ловушку,
 *    см. HexGridRenderer.addGoblinsTrapMarkers): у остальных блестки кружат
 *    вокруг самого гоблина, и итог показывается на нем.
 *
 *  - Кража лепреконов: вокруг отряда-вора по спирали поднимается поток золотых
 *    и изумрудных искр — вместе со всплывающим «+N» украденных монет. Итога
 *    здесь ждать нечего: монеты уже у вора, поток только сопровождает число.
 *
 * Итог способности (смена владельца отряда, урон строению, новая ловушка,
 * плашка с броском) открывается на поле в момент попадания, а не по клику —
 * см. utils/effects/abilityResultHolds.
 *
 * Время — в миллисекундах обычной скорости; размеры — доли размера ячейки.
 */

export const ABILITY_CAST_TEXTURE_KEYS = {
  // Сердечко (белое, красится tint): заряд соблазна, искры, всплывающее сердце
  HEART: 'ability_cast_heart',
  // Половинки расколотого сердечка — по одной ломаной линии, поэтому
  // складываются в целое сердце
  HEART_LEFT: 'ability_cast_heart_left',
  HEART_RIGHT: 'ability_cast_heart_right',
  // Мягкое кольцо: ударная волна, щит, волна очарования
  RING: 'ability_cast_ring',
  // Клуб дыма: неровная мягкая клякса
  PUFF: 'ability_cast_puff',
  // Бомба с фитилем — в своих цветах, не красится
  BOMB: 'ability_cast_bomb',
  // Шевроны улучшения отряда: тот же значок, что на кнопке улучшения
  // (/img/icons/upgrade.png), но толще — закрывают отряд. В своих цветах
  UPGRADE_CHEVRONS: 'ability_cast_upgrade_chevrons',
  // Светящийся диск, плотный до края отряда: за ним незаметно меняется иконка
  SOFT_DISC: 'ability_cast_soft_disc',
  // Черточка с яркой головой справа и тающим хвостом: след стрелы, искра-штрих, разрез
  STREAK: 'ability_cast_streak',
  // Золотая монета в своих цветах: фонтан монет при краже лепреконов
  COIN: 'ability_cast_coin',
  // Череп над погибшим отрядом (белый, красится tint)
  SKULL: 'ability_cast_skull',
};

// Цвета (0x-число — для tint спрайтов)
export const ABILITY_CAST_COLORS = {
  succubes: parseInt(SUCCUBES_SEDUCTION_RESULT_COLOR.slice(1), 16),
  // Сердца суккубов: густой фиолетовый, как у сердец в их следах (не нежно-розовый)
  heart: 0x9b30ff,
  heartLight: 0xc78bff,
  failGray: 0xa3a1ad,
  failSmoke: 0x8a8494,
  goblins: parseInt(GOBLINS_ABILITY_RESULT_COLOR.slice(1), 16),
  goblinsLight: 0xf1f08a,
  upgradeGold: 0xffc21a,
  upgradeLight: 0xfff0a8,
  upgradeAmber: 0xff9a1f,
  silver: 0xc4d6f5,
  silverLight: 0xffffff,
  silverDeep: 0x8fa6cc,
  supply: 0xee8f4f,
  supplyLight: 0xfff3dc,
  supplyAmber: 0xffb454,
  ember: 0xff6a2b,
  leprechaunGold: 0xffc928,
  leprechaunGreen: 0x12a150,
  leprechaunMint: 0xb4f7c6,
  // Темная зелень: блестки гоблинов на светлом песке без нее теряются
  goblinsDark: 0x4f6b16,
  // Темный контур сердечек: на светлых подложках они без него бледнеют
  heartOutline: 0x2a0a4a,
  fireYellow: 0xffd56b,
  fireOrange: 0xff8a2a,
  fireRed: 0xe2482b,
  smoke: 0x5f5a54,
  darkSmoke: 0x3c3834,
  debris: 0x7a6a50,
  white: 0xffffff,
  black: 0x000000,
};

export const SEDUCTION_CAST = {
  // Сбор сердечек вокруг суккуба
  gather: {
    durationMs: 380,
    count: 6,
    sizeFactor: 0.3,
    startRadiusFactor: 0.9,
    endRadiusFactor: 0.15,
    // На сколько оборотов закручиваются, пока сходятся
    turns: 0.4,
    auraSizeFactor: 1.8,
    auraAlpha: 0.7,
  },
  // Полет сердца к цели
  flight: {
    startMs: 340,
    durationMs: 740,
    sizeFactor: 0.7,
    glowSizeFactor: 1.6,
    // Подъем дуги: доля расстояния, но не меньше/не больше долей ячейки
    liftShare: 0.25,
    minLiftFactor: 0.4,
    maxLiftFactor: 1.2,
    // Удары «сердцебиения» за полет и их сила
    beats: 2,
    beatScale: 0.2,
    // След из сердечек и искр
    trailCount: 14,
    trailEveryMs: 50,
    trailLifeMs: 520,
    trailSizeFactor: 0.24,
    trailRiseFactor: 0.35,
  },
  success: {
    burstSizeFactor: 2,
    burstMs: 320,
    ringSizeFactor: 2.8,
    ringMs: 600,
    heartsCount: 10,
    heartsSizeFactor: 0.3,
    heartsSpreadFactor: 1.1,
    heartsRiseFactor: 0.45,
    heartsLifeMs: 800,
    // Большое сердце над целью
    crownSizeFactor: 0.85,
    crownStartLiftFactor: 0.3,
    crownEndLiftFactor: 1,
    crownMs: 1000,
    crownPopMs: 220,
    auraSizeFactor: 2,
    auraMs: 900,
    tailMs: 1000,
  },
  failure: {
    shieldSizeFactor: 1.9,
    shieldMs: 380,
    // Половинки разлетаются и падают
    halvesMs: 720,
    halvesSpreadFactor: 0.6,
    halvesFallFactor: 0.9,
    halvesHopFactor: 0.3,
    halvesTurn: 0.55,
    // За это время половинки теряют цвет
    grayOutMs: 300,
    puffSizeFactor: 1.1,
    puffMs: 650,
    sparksCount: 6,
    sparksLifeMs: 500,
    sparksSizeFactor: 0.14,
    tailMs: 760,
  },
};

export const GOBLINS_BOMB_CAST = {
  windup: {
    durationMs: 220,
    // Бомба появляется над гоблином
    liftFactor: 0.45,
  },
  flight: {
    startMs: 220,
    durationMs: 680,
    sizeFactor: 0.75,
    liftShare: 0.35,
    minLiftFactor: 0.5,
    maxLiftFactor: 1.4,
    // Оборотов за полет: целое число — бомба падает фитилем вверх
    turns: 1,
    // Где горит фитиль относительно центра картинки бомбы (доли ее размера,
    // см. BOMB_FUSE в utils/effects/abilityCastTextures)
    fuseOffsetX: 0.42,
    fuseOffsetY: -0.36,
    fuseGlowSizeFactor: 0.5,
    sparksCount: 16,
    sparksEveryMs: 40,
    sparksLifeMs: 360,
    sparksSizeFactor: 0.14,
    smokeCount: 8,
    smokeEveryMs: 85,
    smokeLifeMs: 600,
    smokeSizeFactor: 0.45,
  },
  explosion: {
    flashSizeFactor: 2.2,
    flashMs: 220,
    fireballSizeFactor: 2.2,
    fireballMs: 420,
    ringSizeFactor: 3,
    ringMs: 520,
    smokeCount: 7,
    smokeSizeFactor: 0.95,
    smokeMinSpreadFactor: 0.35,
    smokeMaxSpreadFactor: 0.85,
    smokeLifeMs: 1100,
    smokeDelayMs: 60,
    embersCount: 12,
    embersSizeFactor: 0.13,
    embersLifeMs: 650,
    // На сколько разлетаются угольки (доли ячейки)
    embersMinSpreadFactor: 0.9,
    embersMaxSpreadFactor: 1.9,
    // Выжженное пятно под взрывом
    scorchSizeFactor: 1.5,
    scorchMs: 1100,
    // Камера вздрагивает от взрыва; при разрушении — сильнее (см. destroyed)
    shakeMs: 180,
    shakeIntensity: 0.0025,
    tailMs: 1200,
  },
  // Разрушение: все крупнее, второй взрыв и дрожь камеры
  destroyed: {
    scale: 1.4,
    smokeCount: 10,
    embersCount: 18,
    secondBlastDelayMs: 130,
    secondBlastOffsetFactor: 0.22,
    shakeMs: 220,
    shakeIntensity: 0.004,
  },
  // Попадание без урона (крепость выдержала): маленький взрыв без угольков
  deflected: {
    scale: 0.6,
  },
  // Провал: бомба подпрыгивает, фитиль тухнет, бомба еще лежит и исчезает
  dud: {
    hopMs: 260,
    hopFactor: 0.25,
    driftFactor: 0.12,
    // На сколько бомба качается в прыжке (радианы)
    wobble: 0.35,
    fuseOutMs: 480,
    sputterCount: 4,
    // Струйка дыма от потухшего фитиля
    wispCount: 3,
    wispSizeFactor: 0.24,
    wispEveryMs: 110,
    wispLifeMs: 650,
    // Сколько бомба лежит после того, как фитиль потух, и за сколько исчезает
    fadeDelayMs: 280,
    fadeMs: 260,
    tailMs: 1350,
  },
};

/**
 * УЛУЧШЕНИЕ ОТРЯДА: снизу наплывают золотые шевроны, в момент, когда они закрыли
 * отряд, под ними и за светящимся диском меняется его иконка (impactMs), вспышка,
 * лучи и кольца, затем шевроны уходят вверх и гаснут
 */
export const UNIT_UPGRADE_CAST = {
  impactMs: 520,
  totalMs: 1550,
  chevrons: {
    // Ширина картинки: значок шире отряда
    sizeFactor: 1.4,
    // Откуда наплывают (вниз от центра отряда) и докуда уходят (вверх)
    startOffsetFactor: 0.75,
    endLiftFactor: 1,
    appearMs: 170,
    fromScale: 0.7,
    peakScale: 1.14,
    // Доля времени после смены иконки, пока шевроны еще стоят на отряде
    holdShare: 0.16,
    fadeFromShare: 0.5,
    glowSizeFactor: 2.2,
    glowAlpha: 0.85,
  },
  // Диск за шевронами: к смене иконки становится непрозрачным и прячет ее
  cover: {
    sizeFactor: 1.9,
    appearFromMs: 200,
    fullAtMs: 430,
    fullUntilMs: 640,
    goneAtMs: 1000,
  },
  flash: {sizeFactor: 2.6, leadMs: 40, durationMs: 300},
  rays: {sizeFactor: 2.9, startMs: 120, durationMs: 1150, turn: 0.7, alpha: 0.9},
  aura: {sizeFactor: 2.6, alpha: 0.9},
  rings: {sizeFactor: 3.2, durationMs: 620, secondDelayMs: 130, alpha: 0.95},
  // Восходящий поток золотых искр вокруг отряда
  stream: {
    count: 30,
    spawnMs: 950,
    lifeMs: 600,
    minSizeFactor: 0.16,
    maxSizeFactor: 0.3,
    startRadiusFactor: 0.75,
    endRadiusFactor: 0.25,
    turns: 0.8,
    swirlFlatten: 0.3,
    topJitterFactor: 0.3,
    topLiftFactor: 1.25,
  },
  // Звездочки разлетаются в момент смены иконки
  burst: {count: 14, sizeFactor: 0.3, spreadFactor: 1.45, lifeMs: 720},
};

/**
 * ПЕРЕДАЧА РАЗМЕРА от квартала отряду (докупка размера на этапе найма): маркер на
 * квартале растворяется в свете, искры сходятся в шар с человечками, шар по дуге
 * летит к отряду, за ним тянется золотой след. Шар долетел (impactMs) — на отряде
 * вспышка, кольца и восходящий поток, и тогда у него появляется «+N» и всплывает
 * потраченное. Прибавка больше 1 (арсенал) — по шару на каждую единицу.
 */
export const UNIT_SIZE_TRANSFER_CAST = {
  launchMs: 170,
  flightMs: 430,
  orbGapMs: 110,
  maxOrbs: 3,
  tailMs: 880,
  source: {
    plateMs: 240,
    plateGrow: 0.22,
    flash: {sizeFactor: 2.3, durationMs: 300, alpha: 0.95},
    aura: {sizeFactor: 2.5, durationMs: 620, alpha: 0.9},
    ring: {sizeFactor: 2.7, durationMs: 460, alpha: 0.95},
    // Искры сходятся к месту, откуда стартует шар
    motes: {count: 12, spreadFactor: 1, sizeFactor: 0.26, turn: 1.1},
  },
  orb: {
    // Ореол не шире ячейки: иначе белит отряд, к которому шар подлетает
    glowSizeFactor: 1.5,
    glowAlpha: 0.9,
    raysSizeFactor: 2.1,
    raysAlpha: 1,
    raysTurn: 5,
    coreSizeFactor: 1.05,
    iconSizeFactor: 0.6,
    iconTint: 0x5a2c00,
    bornScale: 0.35,
    landingScale: 0.85,
    // Дуга полета: короткий путь не плоский, дальний не улетает за кадр
    arc: {liftShare: 0.3, minLiftFactor: 0.3, maxLiftFactor: 1},
  },
  // След: искры остаются на пути шара и медленно всплывают
  trail: {count: 30, lifeMs: 520, minSizeFactor: 0.26, maxSizeFactor: 0.5, jitterFactor: 0.34, driftFactor: 0.3},
  // Хвост кометы: сплошная золотая полоса за шаром, тает быстрее искр
  comet: {count: 16, lifeMs: 240, fromSizeFactor: 0.72, toSizeFactor: 0.3},
  arrival: {
    flash: {sizeFactor: 2.5, leadMs: 30, durationMs: 300},
    // Золотой диск на миг накрывает отряд: прибавка вошла в него
    disc: {sizeFactor: 1.6, durationMs: 340, alpha: 0.95},
    aura: {sizeFactor: 2.5, durationMs: 700, alpha: 0.95},
    rays: {sizeFactor: 2.9, durationMs: 620, turn: 0.6, alpha: 1},
    rings: {sizeFactor: 2.9, durationMs: 560, secondDelayMs: 110, alpha: 0.95},
    stream: {
      count: 16,
      spawnMs: 360,
      lifeMs: 520,
      minSizeFactor: 0.24,
      maxSizeFactor: 0.42,
      startRadiusFactor: 0.65,
      endRadiusFactor: 0.2,
      turns: 0.7,
      swirlFlatten: 0.3,
      topJitterFactor: 0.3,
      topLiftFactor: 1.05,
    },
    burst: {count: 12, sizeFactor: 0.4, spreadFactor: 1.35, lifeMs: 620},
  },
};

/**
 * ПЕРЕДАЧА СНАБЖЕНИЯ от фермы отряду: шар с колосом идет по нити пути (за то же
 * время, что нить тянется, см. resolveThreadRevealMs) и вспыхивает на отряде
 */
export const SUPPLY_TRANSFER_CAST = {
  tailMs: 620,
  orb: {
    glowSizeFactor: 1.4,
    glowAlpha: 0.95,
    raysSizeFactor: 1.8,
    raysAlpha: 0.95,
    raysTurn: 4,
    coreSizeFactor: 0.92,
    iconSizeFactor: 0.56,
    bornScale: 0.4,
    bornMs: 140,
  },
  comet: {count: 18, lifeMs: 230, fromSizeFactor: 0.6, toSizeFactor: 0.28},
  trail: {count: 22, lifeMs: 460, minSizeFactor: 0.2, maxSizeFactor: 0.4, jitterFactor: 0.3, driftFactor: 0.26},
  arrival: {
    aura: {sizeFactor: 2.2, durationMs: 560, alpha: 0.9},
    disc: {sizeFactor: 1.3, durationMs: 300, alpha: 0.9},
    ring: {sizeFactor: 2.5, durationMs: 500, alpha: 0.95},
    burst: {count: 10, sizeFactor: 0.32, spreadFactor: 1.15, lifeMs: 520},
  },
};

/**
 * ОБРЫВ ПУТИ СНАБЖЕНИЯ: на границе, докуда ферма дотянулась, нить рвется
 * вспышкой искр, и дальше она тянется уже погасшей
 */
export const SUPPLY_BREAK_CAST = {
  flash: {sizeFactor: 1.5, durationMs: 260, alpha: 0.95},
  ring: {sizeFactor: 1.9, durationMs: 380, alpha: 0.9},
  sparks: {count: 12, sizeFactor: 0.3, spreadFactor: 1.05, lifeMs: 520, fallFactor: 0.45},
};

/**
 * РАЗОВЫЙ ПОКАЗ ДОХОДА (постройка гильдии и храма, рост города): кольцо от
 * медальона и восходящий поток искр цвета ресурса
 */
export const INCOME_REVEAL_CAST = {
  totalMs: 1050,
  aura: {sizeFactor: 2.3, durationMs: 800, alpha: 0.85},
  ring: {sizeFactor: 2.6, durationMs: 560, alpha: 0.95},
  stream: {
    count: 18,
    spawnMs: 480,
    lifeMs: 560,
    minSizeFactor: 0.2,
    maxSizeFactor: 0.38,
    startRadiusFactor: 0.7,
    endRadiusFactor: 0.25,
    turns: 0.7,
    swirlFlatten: 0.3,
    topJitterFactor: 0.3,
    topLiftFactor: 1,
  },
};

export const LEPRECHAUNS_THEFT_CAST = {
  totalMs: 1350,
  // Искры вылетают не разом, а струей
  particles: {
    count: 26,
    spawnMs: 600,
    lifeMs: 750,
    minSizeFactor: 0.16,
    maxSizeFactor: 0.28,
    // Радиус спирали вокруг отряда: внизу шире, к верху сужается
    startRadiusFactor: 0.6,
    endRadiusFactor: 0.2,
    turns: 0.8,
    // Сплющивание круга спирали по высоте и разброс высоты конца пути (доли ячейки)
    swirlFlatten: 0.3,
    topJitterFactor: 0.2,
  },
  // Столб света от земли до числа
  column: {
    durationMs: 1000,
    sizeFactor: 1.5,
    stretch: 2,
    alpha: 0.5,
  },
  // Вспышка-кольцо у ног отряда
  ring: {
    durationMs: 520,
    sizeFactor: 1.5,
    alpha: 0.85,
  },
};

export const GOBLINS_TRAP_CAST = {
  swirl: {
    durationMs: 260,
    count: 8,
    sizeFactor: 0.15,
    startRadiusFactor: 0.7,
    endRadiusFactor: 0.25,
    turns: 0.6,
  },
  flight: {
    startMs: 200,
    durationMs: 560,
    sizeFactor: 0.75,
    liftShare: 0.3,
    minLiftFactor: 0.4,
    maxLiftFactor: 1.1,
    trailCount: 12,
    trailEveryMs: 45,
    trailLifeMs: 420,
    trailSizeFactor: 0.15,
    trailFallFactor: 0.25,
  },
  success: {
    // Контур ячейки вспыхивает и «защелкивается» к своему размеру: по краю
    // ловушки (доля радиуса ячейки), чуть с перехлестом и обратно
    outlineRadiusRatio: 0.9,
    outlineFromScale: 0.72,
    outlineOvershoot: 1.06,
    outlineMs: 760,
    outlineWidthFactor: 0.1,
    snapMs: 160,
    burstSizeFactor: 1.4,
    burstMs: 300,
    sparksCount: 8,
    sparksSizeFactor: 0.16,
    sparksSpreadFactor: 0.8,
    sparksLifeMs: 560,
    tailMs: 800,
  },
  failure: {
    puffSizeFactor: 1.1,
    puffMs: 650,
    debrisCount: 6,
    debrisSizeFactor: 0.12,
    debrisLifeMs: 560,
    outlineMs: 360,
    tailMs: 700,
  },
  // Чужая ловушка: где она, не показываем — блестки кружат вокруг гоблина
  hidden: {
    swirlMs: 420,
    count: 10,
    resultSizeFactor: 1.2,
    sparksCount: 6,
    tailMs: 640,
  },
};

/**
 * Смешок гоблина при успехе ловушки и саботажа — тот же, что при выборе отряда
 * в облике фракции (configs/factionUnitSounds). У наездников d8 и d12 там вой
 * волка и визг кабана, поэтому они смеются как пехота того же ранга, d6 и d10.
 * Катапульта своего голоса не имеет — смеется как d6
 *
 * @param {string} [unitType] — тип отряда (UNIT_TYPES.*.id)
 * @returns {string} имя отложенного звука (configs/deferredSounds)
 */
export const resolveGoblinsSuccessSoundId = (unitType) => ({
  [UNIT_TYPES.D10.id]: 'ability_goblins_success_d10',
  [UNIT_TYPES.D12.id]: 'ability_goblins_success_d10',
  [UNIT_TYPES.D20.id]: 'ability_goblins_success_d20',
}[unitType] || 'ability_goblins_success_d6');

/**
 * Смех лепрекона при краже монет — тот же, что при найме и выборе отряда в
 * облике фракции (configs/factionUnitSounds). У кавалерии d8 и d12 там не смех,
 * поэтому она смеется как пехота того же ранга, d6 и d10. Катапульта своего
 * голоса не имеет — смеется как d6
 *
 * @param {string} [unitType] — тип отряда (UNIT_TYPES.*.id)
 * @returns {string} имя отложенного звука (configs/deferredSounds)
 */
export const resolveLeprechaunsTheftLaughSoundId = (unitType) => ({
  [UNIT_TYPES.D10.id]: 'ability_leprechauns_laugh_d10',
  [UNIT_TYPES.D12.id]: 'ability_leprechauns_laugh_d10',
  [UNIT_TYPES.D20.id]: 'ability_leprechauns_laugh_d20',
}[unitType] || 'ability_leprechauns_laugh_d6');

// Контур сердечек: во сколько раз темная подложка крупнее самого сердечка
export const ABILITY_CAST_HEART_OUTLINE_SCALE = 1.16;

// Где рисуется итог на отряде: центр силуэта чуть выше центра ячейки (доля ячейки)
export const ABILITY_CAST_UNIT_BODY_LIFT_FACTOR = 0.12;

/**
 * Темп чужих действий (боты, соперник по сети): итог должен открыться в
 * пределах этой доли шага до следующего действия. Не укладывается даже
 * сжатым до minTimeScale — анимации нет, итог виден сразу (как у выстрела,
 * см. scenes/playShotAnimation)
 */
export const ABILITY_CAST_PACE = {
  impactShareOfPace: 0.75,
  minTimeScale: 0.45,
};

// Подстраховка удержания итога: анимация, застрявшая дольше своей
// длительности на столько (скрытая вкладка — кадры стоят), итог все равно
// открывает
export const ABILITY_RESULT_HOLD_MARGIN_MS = 1500;
